import { useMemo, useState } from 'react';

import { Download, Plus, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { IrregularidadeDaDocumentacao } from '@/modulos/operacao/cadastros/IrregularidadeDaDocumentacao';
import { SEM_PERMISSOES } from '@/modulos/operacao/cadastros/leitura';
import { UltimaSincronizacaoCell } from '@/modulos/operacao/comum/UltimaSincronizacaoCell';
import { OPCOES_DE_SITUACAO_DA_PESSOA, situacaoComoSelo } from '@/modulos/operacao/comum/contrato';
import { useRecursosAtivosEmOs } from '@/modulos/operacao/comum/dados';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';

import { useBaixarMonitoresCsv, useMonitores } from './dados';
import { MonitorFormulario } from './MonitorFormulario';
import type { FiltroDeMonitores, Monitor } from './contrato';

/**
 * `/operacao/monitores` — quem acompanha os estudantes dentro do veiculo.
 *
 * A LISTA E MAIS ENXUTA QUE A DE CONDUTORES DE PROPOSITO, e as duas ausencias sao
 * decisao, nao esquecimento:
 *
 * - SEM COLUNA DE CREDENCIADO E SEM COLUNA DE DIRETORIA. O monitor pode acompanhar rotas
 *   de mais de um credenciado, e o vinculo vive numa tabela com historico. Mostrar "o"
 *   credenciado obrigaria a tela a escolher um entre varios — pareceria dado e seria
 *   chute.
 * - SEM CNH E SEM CRMC NA CONTA DE IRREGULARIDADE. Monitor nao dirige. Cobrar dele
 *   habilitacao de condutor marcaria a lista inteira de vermelho sem que nada estivesse
 *   errado, e um alerta que sempre acende deixa de ser lido.
 *
 * "NOVO MONITOR" ABRE O FORMULARIO, MAS O "SALVAR" NAO GRAVA NADA AINDA. Nenhuma
 * tabela cadastral aceita escrita hoje — nem de `ADMIN` (docs/ALVOS.md, Onda 2) —, e
 * nao ha rota de criacao na API. O dono do repositorio decidiu, em 2026-08-20, que a
 * interface pode ficar pronta antes do backend: o botao e o formulario existem, e o
 * "Salvar" mostra um aviso honesto em vez de fingir sucesso
 * (`comum/cadastroIndisponivel.ts`).
 *
 * LE DA API (`GET bff/v1/monitores`), sob o recorte do banco (0012). O CPF chega
 * mascarado. Sem leitura da documentacao da J-05, a coluna de irregularidade nao aparece;
 * sem leitura de OS, o "Ativo em OS" tambem nao (regra 7 de interface).
 *
 * O clique na linha abre `/operacao/monitores/:slug` — o detalhe (`CadastroDetalhePage`),
 * so leitura.
 */
export function MonitoresPage() {
  const navigate = useNavigate();

  const padroes = useMemo(() => ({ search: '', status: '', ativoOs: '' }), []);
  const { values, setFilter, clearAll, page, setPage, perPage, setPerPage } =
    useUrlFilters(padroes);

  const filtro = useMemo<FiltroDeMonitores>(
    () => ({
      pagina: page,
      tamanho: perPage,
      busca: values.search || undefined,
      situacao: values.status || undefined,
      ativoEmOs: values.ativoOs === 'sim' ? true : undefined,
    }),
    [page, perPage, values.search, values.status, values.ativoOs],
  );

  const { data, isLoading, isError, error, refetch } = useMonitores(filtro);
  const permissoes = data?.permissoes ?? SEM_PERMISSOES;

  // Ver o comentario equivalente em CondutoresPage: o filtro roda no servidor, e o selo
  // "Em OS" da linha vem de `/ordens-de-servico/recursos-ativos`, so para quem le OS.
  const { data: recursosAtivos } = useRecursosAtivosEmOs(permissoes.ordensDeServico);

  const linhas = useMemo(() => data?.itens ?? [], [data?.itens]);

  const temFiltro = Boolean(values.search || values.status || values.ativoOs);

  const campos: FilterFieldConfig[] = [
    {
      key: 'search',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nome ou CPF…',
      ariaLabel: 'Buscar monitores',
      flex: true,
    },
    ...(permissoes.ordensDeServico
      ? [
          {
            key: 'ativoOs',
            label: 'Ativo em OS',
            type: 'select' as const,
            options: [{ value: 'sim', label: 'Apenas ativos em OS' }],
            width: 'w-full sm:w-[180px]',
          },
        ]
      : []),
    {
      key: 'status',
      label: 'Situação',
      type: 'select',
      options: [...OPCOES_DE_SITUACAO_DA_PESSOA],
      width: 'w-full sm:w-[150px]',
    },
  ];

  const colunas = useMemo<TableViewColumn<Monitor>[]>(
    () => [
      {
        id: 'nome',
        header: 'Nome',
        cell: (monitor) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{monitor.nome}</span>
            {recursosAtivos?.monitores.has(monitor.id) && (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                Em OS
              </Badge>
            )}
          </div>
        ),
        sortKey: 'nome',
        isCritical: true,
        canHide: false,
      },
      {
        id: 'cpf',
        header: 'CPF',
        // Mascarado pelo banco: o CPF completo nao sai da API.
        cell: (monitor) => <span className="font-mono text-sm">{monitor.cpf ?? '—'}</span>,
      },
      ...(permissoes.documentacao
        ? [
            {
              id: 'irregularidade',
              header: 'Irregularidade',
              // Sem `semVinculo`: o vinculo do monitor e por rota, e cobrar vinculo direto
              // marcaria de ambar a lista inteira sem que nada estivesse errado.
              cell: (monitor: Monitor) => (
                <IrregularidadeDaDocumentacao
                  validades={monitor.validades ?? []}
                  situacao={monitor.status}
                />
              ),
              tooltip:
                'Validade aprovada de cada documento em vigor, na análise do DTP. Monitor não dirige, então CNH e CRMC não entram na conta.',
            },
          ]
        : []),
      {
        id: 'ultima_sincronizacao',
        header: 'Última sincronização',
        cell: (monitor) => (
          <UltimaSincronizacaoCell
            origem={monitor.origem_dado}
            sincronizadoEm={monitor.sincronizado_em}
            emContingencia={monitor.em_contingencia}
          />
        ),
        sortKey: 'sincronizado_em',
      },
      {
        id: 'status',
        header: 'Situação',
        cell: (monitor) => (
          <StatusBadge status={situacaoComoSelo(monitor.status)} label={monitor.status} />
        ),
        sortKey: 'status',
        linkedFilterKeys: ['status'],
        isCritical: true,
        canHide: false,
        isStatusColumn: true,
      },
    ],
    [permissoes.documentacao, recursosAtivos],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'monitores:list',
    columns: colunas,
    filterValues: values,
  });

  const baixarCsv = useBaixarMonitoresCsv();
  const [baixando, setBaixando] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);

  const exportar = async () => {
    setBaixando(true);
    try {
      const quantidade = await baixarCsv(filtro);
      toast('Planilha gerada', {
        description: `${quantidade} monitor(es) no arquivo, com os filtros que você está usando.`,
      });
    } catch {
      toast.error('Não foi possível gerar o arquivo', {
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Monitores"
        description="Quem acompanha os estudantes no veículo, com os cursos obrigatórios em dia."
      >
        <Button
          variant="outline"
          onClick={() => void exportar()}
          disabled={baixando || isLoading}
          aria-label="Exportar monitores em CSV"
        >
          <Download className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">{baixando ? 'Gerando…' : 'Exportar CSV'}</span>
        </Button>
        <Button onClick={() => setNovoAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Novo Monitor</span>
        </Button>
      </PageHeader>

      <ResponsiveModal
        open={novoAberto}
        onOpenChange={setNovoAberto}
        title={
          <>
            <UserRound className="h-5 w-5 text-primary" aria-hidden="true" />
            Novo Monitor
          </>
        }
        description="Cadastre um novo monitor no programa TEG."
        desktopMaxWidth="max-w-4xl"
      >
        <MonitorFormulario onCancelar={() => setNovoAberto(false)} />
      </ResponsiveModal>

      <FilterBar
        fields={campos}
        values={values}
        onChange={setFilter}
        onClear={clearAll}
        defaultValues={padroes}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível buscar os monitores agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns as DataTableColumn<Monitor>[]}
          personalizacao={tableView}
          data={linhas}
          onRowClick={(monitor) => navigate(`/operacao/monitores/${monitor.slug}`)}
          isLoading={isLoading}
          total={data?.total}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          emptyIcon={UserRound}
          emptyTitle="Nenhum monitor encontrado"
          emptyDescription={
            temFiltro
              ? 'Nenhum monitor atende a esses filtros. Tente afrouxar a busca.'
              : 'Quando houver monitor cadastrado, ele aparece aqui.'
          }
          minWidth="640px"
          ariaLabel="Lista de monitores"
        />
      )}
    </div>
  );
}

export default MonitoresPage;
